import { S } from '../../i18n/strings';
import type { Leaf } from '../../i18n/strings';

/**
 * Les outils de la palette, dans l'ordre du handoff — `nav` en tête.
 *
 * Le handoff n'en prévoyait que onze, et confiait deux rôles au même outil :
 * délimiter une zone, et laisser passer les clics pour que le site reste
 * utilisable. Les deux s'excluent — un outil ne peut pas à la fois ignorer le
 * pointeur et le capter — et c'est la sélection qui perdait : elle n'a jamais
 * rien pu tracer. `nav` reprend donc la navigation, et les ciseaux ne font plus
 * que ce que leur glyphe promet.
 */
export const TOOLS = [
  'nav',
  'pencil',
  'fill',
  'spray',
  'rect',
  'ellipse',
  'line',
  'text',
  'select',
  'picker',
  'eraser',
  'stamp',
] as const;

export type Tool = (typeof TOOLS)[number];

export function isTool(value: unknown): value is Tool {
  return typeof value === 'string' && (TOOLS as readonly string[]).includes(value);
}

/**
 * Les outils dont le pictogramme est tracé plutôt qu'écrit.
 *
 * Unicode n'a ni pipette ni flèche de curseur : leurs glyphes de repli se
 * lisaient l'un comme un camembert, l'autre comme une flèche de direction. Le
 * dessin vit dans `ToolIcons.tsx` ; ici on note seulement lesquels le portent,
 * pour que ce module reste sans JSX.
 *
 * Leur entrée dans `TOOL_INFO` garde tout de même un glyphe : il sert de repli
 * et de valeur de comparaison, jamais d'affichage.
 */
export const DRAWN_TOOLS = ['nav', 'picker'] as const;

/**
 * Glyphe, libellé et curseur de chaque outil.
 *
 * Les glyphes sont ceux du handoff : des caractères Unicode, aucune icône à
 * charger. Le curseur remplace celui du site tant qu'un outil dessine.
 */
export const TOOL_INFO: Record<Tool, { readonly glyph: string; readonly label: Leaf; readonly cursor: string }> = {
  nav: { glyph: '↖', label: S.paint.tools.nav, cursor: 'default' },
  pencil: { glyph: '✎', label: S.paint.tools.pencil, cursor: 'crosshair' },
  fill: { glyph: '▨', label: S.paint.tools.fill, cursor: 'crosshair' },
  spray: { glyph: '◍', label: S.paint.tools.spray, cursor: 'crosshair' },
  rect: { glyph: '▭', label: S.paint.tools.rect, cursor: 'crosshair' },
  ellipse: { glyph: '◯', label: S.paint.tools.ellipse, cursor: 'crosshair' },
  line: { glyph: '╱', label: S.paint.tools.line, cursor: 'crosshair' },
  text: { glyph: 'А', label: S.paint.tools.text, cursor: 'text' },
  select: { glyph: '✂', label: S.paint.tools.select, cursor: 'crosshair' },
  picker: { glyph: '◔', label: S.paint.tools.picker, cursor: 'crosshair' },
  eraser: { glyph: '⌫', label: S.paint.tools.eraser, cursor: 'cell' },
  stamp: { glyph: '🐟', label: S.paint.tools.stamp, cursor: 'copy' },
};

/**
 * `nav` est le mode navigation : la couche de dessin laisse passer les clics, le
 * site est pleinement utilisable. C'est l'état de départ, et la sortie de
 * secours d'`Échap`.
 */
export const DEFAULT_TOOL: Tool = 'nav';

/**
 * Vrai si l'outil a besoin du pointeur — donc si la couche doit le capter.
 *
 * C'est la seule question que se posent la feuille de style et le début de
 * `onPointerDown`, et elle ne se confond pas avec « cet outil peint » : la
 * sélection capte le pointeur sans jamais rien écrire dans le calque, elle ne
 * trace que dans l'aperçu.
 */
export function usesPointer(tool: Tool): boolean {
  return tool !== 'nav';
}
